import * as React from "react";
import { Alert, Button, Card, Checkbox, Divider, notification, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../components/antd/card/CardContent";
import { CloudAccountAwsConfig } from "../../../../schema/types/cloudaccount/entity";
import { SyntaxHighlight } from "../../../../components/syntaxHighlight/SyntaxHighlight";
import { request } from "../../../../services/cpln";
import { FormItemDescription } from "../../../../components/antd/form/FormItemDescription";
import { SyncOutlined } from "@ant-design/icons";
import { CardCollapsibleContent } from "../../../../components/antd/card/CardCollapsibleContent";
import { useDrawerContext } from "../../../../components/antd/drawerContext";
import { IdentityInput } from "../../../../schema/types/identity/input";
import { useAppSelector } from "../../../../store/hooks";
import { CopyButtonWithApproval } from "../../../domain/create/copyButtonWithApproval";
import { BrowsedItem } from "../../../../store/slices/browserServiceSlice";
import { formSetValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpSwitch } from "../../../gvc/testNewForm/CpSwitch";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { CpDescription } from "../../../gvc/testNewForm/CpDescription";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";
import { CpRadioGroup } from "../../../gvc/testNewForm/CpRadioGroup";
import { CpInput } from "../../../gvc/testNewForm/CpInput";
import { CpArrayString } from "../../../gvc/testNewForm/CpArrayString";
import { CpCheckboxGroup } from "../../../gvc/testNewForm/CpCheckboxGroup";
import { store } from "../../../../store";
import { CpErrorWarningArrayIcon } from "../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { ExternalLink } from "../../../../newcomponents/table/components/ExternalLink";
import { DOCS_URL } from "../../../../envVariables";
import { parseLink } from "../../../../utils/linkParser/linkParser";
import { CpKindSelect } from "../../../gvc/testNewForm/temp/CpKindSelect";

interface Props {}
export const IdentityCreateCloudaccessAWS: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const theme = useAppSelector((state) => state.theme.theme);

  const awsLogoPath = `/resources/logos/awsLogo${theme === "dark" ? "Dark" : ""}.png`;

  const drawer = useDrawerContext();

  const isEnabled = useCpFormValue(`aws.isEnabled`);
  const method = useCpFormValue(`aws.method`);
  const ca = useCpFormValue(`aws.cloudAccount`);
  const role = useCpFormValue(`aws.roleName`);

  const policyFilter = useCpFormValue(`aws.policyRefs.filter`);
  const selectedPolicies = useCpFormValue(`aws.policyRefs.selectedPolicies`);
  const policyRefsError = useCpFormValue(`aws.policyRefs.error`);

  const [isFetchingRoles, setIsFetchingRoles] = React.useState(false);
  const [roles, setRoles] = React.useState<BrowsedItem[]>([]);
  React.useEffect(() => {
    if (method === "role") {
      fetchRoles();
    }
  }, [ca.link, method]);
  async function fetchRoles() {
    try {
      setIsFetchingRoles(true);
      const servicesRes = await request({ service: "browser", url: `/browse${ca.link}` });
      const iamLink = servicesRes.data.links.find((l) => l.label === "IAM")?.href;
      if (!iamLink) {
        throw new Error("Cannot reach IAM service.");
      }
      let allRoles: BrowsedItem[] = [];
      let nextLink: string | undefined = `${iamLink}/global/role`;
      while (nextLink) {
        const res = await request({ service: "browser", url: nextLink });
        allRoles = allRoles.concat(res.data.data || []);
        nextLink = res.data.links?.find((l: any) => l.rel === "next")?.href;
      }
      setRoles(allRoles);
      formSetValue(id, `aws.roleName.error`, "");
      setIsFetchingRoles(false);
    } catch (e) {
      setIsFetchingRoles(false);
      setRoles([]);
      formSetValue(id, `aws.roleName.error`, e.message);
    }
  }

  const [isFetchingPolicies, setIsFetchingPolicies] = React.useState(false);
  const [policies, setPolicies] = React.useState<BrowsedItem[]>([]);
  React.useEffect(() => {
    if (method === "policies") {
      fetchPolicies();
    }
  }, [ca.link, method]);
  async function fetchPolicies() {
    try {
      setIsFetchingPolicies(true);
      const servicesRes = await request({ service: "browser", url: `/browse${ca.link}` });
      const iamLink = servicesRes.data.links.find((l) => l.label === "IAM")?.href;
      if (!iamLink) {
        throw new Error("Cannot reach IAM service.");
      }
      let allPolicies: BrowsedItem[] = [];
      let nextLink: string | undefined = `${iamLink}/global/policy`;
      while (nextLink) {
        const res = await request({ service: "browser", url: nextLink });
        allPolicies = allPolicies.concat(res.data.data || []);
        nextLink = res.data.links?.find((l: any) => l.rel === "next")?.href;
      }
      setPolicies(allPolicies);
      formSetValue(id, `aws.policyRefs.error`, "");
      formSetValue(id, `aws.policyRefs.inputMode`, "selection");
      setIsFetchingPolicies(false);
    } catch (e) {
      setIsFetchingPolicies(false);
      setPolicies([]);
      formSetValue(id, `aws.policyRefs.inputMode`, "manual");
      formSetValue(id, `aws.policyRefs.error`, e.message);
    }
  }

  const fieldValueRoleArn = (ca.item?.data as CloudAccountAwsConfig)?.roleArn || "<replaceWith-roleArnOfCloudAccount>";

  const caName = parseLink(ca.link, { useInputCtx: true }).name;

  function getRoleTrustPolicy() {
    return `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Effect": "Allow",
      "Principal": {
        "AWS": [
          "${fieldValueRoleArn}"
        ]
      },
      "Action": "sts:AssumeRole"
    }
  ]
}`;
  }

  const filteredPolicies = policies.filter((option) => {
    return (
      selectedPolicies.includes(option._cpln.ref) ||
      option._cpln.label.toLowerCase().includes(policyFilter.toLowerCase()) ||
      option._cpln.ref.toLowerCase().includes(policyFilter.toLowerCase())
    );
  });

  const unmatchedPolicies = React.useMemo(() => {
    if (selectedPolicies.length === 0 || policies.length === 0) return [];
    const policyRefSet = new Set(policies.map((p) => p._cpln.ref));
    return selectedPolicies.filter((ref: string) => !policyRefSet.has(ref));
  }, [selectedPolicies, policies]);

  const unmatchedRole = React.useMemo(() => {
    if (!role.value || roles.length === 0) return false;
    return !roles.some((r) => r._cpln.ref === role.value);
  }, [role.value, roles]);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            <div className={`flex items-center`}>
              <img
                aria-label="AWS Cloud Access"
                style={{ width: "auto", height: 36 }}
                src={awsLogoPath}
                className="self-center"
              />
              <CpErrorWarningArrayIcon names={["aws"]} />
            </div>
          </CardTitle>

          <CardContent>
            <CpFormItem>
              <div className="flex items-center gap-2">
                <CpLabel name={`aws.isEnabled`}>Configure</CpLabel>
                <CpSwitch name={`aws.isEnabled`} />
              </div>
            </CpFormItem>
            <CardCollapsibleContent collapsed={!isEnabled}>
              <CpFormItem>
                <CpLabel name={`aws.cloudAccount`}>
                  AWS Cloud Account
                  <span
                    className="text-sm cursor-pointer color-link ml-2"
                    onClick={() => {
                      drawer.setTemporary(
                        `identity-cloudaccess-aws-cloudaccount`,
                        "Cloud Account",
                        <>
                          <div>
                            A cloud account links your organization to a cloud provider by creating a minimally
                            privileged bridge account that Control Plane uses to manage resources.
                          </div>
                          <div className="mt-2">
                            In conjunction with identities, cloud accounts enforce access rules so your workload can
                            transparently access approved resources regardless of its location. For example, if your
                            application uses AWS S3 and Azure Cosmos DB, the workload seamlessly connects to both.
                          </div>
                          <div className="mt-2">
                            <ExternalLink
                              label="Click here to learn more about Cloud Accounts"
                              url={`${DOCS_URL}/reference/cloudaccount`}
                              disableCopy
                            />
                          </div>
                        </>,
                      );
                    }}
                  >
                    Info
                  </span>
                </CpLabel>
                <CpDescription>AWS cloud account is used to authenticate with AWS Services.</CpDescription>
                <CpKindSelect
                  kind="cloudaccount"
                  // TODO can limit here the create for to aws option
                  optionValue="name"
                  style={{ maxWidth: 600 }}
                  placeholder={"Select or type a Cloud Account Item"}
                  name={`aws.cloudAccount`}
                  queries={[{ property: "provider", value: "aws" }]}
                />
                <CpErrorWarning name={`aws.cloudAccount`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`aws.method`}>Method</CpLabel>
                <CpRadioGroup
                  name={`aws.method`}
                  options={[
                    { label: "Use an Existing AWS Role", value: "role" },
                    { label: "Select Existing AWS Policies", value: "policies" },
                  ]}
                />
                <CpErrorWarning name={`aws.method`} />
              </CpFormItem>
              {method === "role" ? (
                <>
                  <Divider orientation={"left"} orientationMargin={0}>
                    Continue with an Existing AWS Role
                  </Divider>
                  <Alert
                    className="mt-2 mb-4"
                    type={"info"}
                    message={
                      <div className="flex flex-col">
                        <div>
                          Make sure the selected role has a <span className="font-semibold">Trust Policy</span> allowing
                          the <span className="font-semibold">Role ARN</span> of the referenced{" "}
                          <span className="font-semibold">Cloud Account ({ca.name})</span> to assume it.
                        </div>
                      </div>
                    }
                  />
                  <SyntaxHighlight
                    language="json"
                    code={getRoleTrustPolicy()}
                  />
                  <Button
                    htmlType={`button`}
                    className="mt-2"
                    onClick={() => {
                      navigator.clipboard.writeText(getRoleTrustPolicy());
                      notification.info({ message: "Copied Trust Policy", duration: 1, closable: false });
                    }}
                  >
                    Copy Trust Policy
                  </Button>
                  {fieldValueRoleArn ? (
                    <div className="mt-6">
                      <div>Role Arn of Cloud Account</div>
                      <div className="flex items-center gap-2">
                        <div>{fieldValueRoleArn}</div>
                        <CopyButtonWithApproval value={fieldValueRoleArn} />
                      </div>
                    </div>
                  ) : null}
                  <Divider />
                  {role.error ? (
                    <>
                      <CpFormItem>
                        <CpLabel name="aws.roleName.value">Role Name</CpLabel>
                        <CpInput name="aws.roleName.value" />
                        <CpErrorWarning name="aws.roleName.value" />
                      </CpFormItem>
                      <Alert
                        className="mt-2"
                        type={"warning"}
                        showIcon
                        message={`Selected Cloud Account Failed to List Roles`}
                        description={role.error}
                      />
                    </>
                  ) : (
                    <>
                      <FormItemDescription>{role.value || "Select a Role"}</FormItemDescription>
                      {unmatchedRole && (
                        <Alert
                          className="mt-2 mb-4"
                          type="warning"
                          showIcon
                          message="Unrecognized Role"
                          description={
                            <>
                              The configured role <code>{role.value}</code> was not found in the AWS role list.
                            </>
                          }
                        />
                      )}
                      <CpFormItem>
                        <CpLabel name="aws.roleName.filter">Filter Roles</CpLabel>
                        <Space.Compact>
                          <CpInput placeholder="Filter Roles" name="aws.roleName.filter" style={{ width: 300 }} />
                          <Tooltip title={`Refetch Roles`}>
                            <Button
                              icon={<SyncOutlined />}
                              onClick={() => fetchRoles()}
                              loading={isFetchingRoles}
                              disabled={isFetchingRoles}
                            />
                          </Tooltip>
                        </Space.Compact>
                      </CpFormItem>
                      <CpFormItem>
                        <CpLabel name={`aws.roleName.value`}></CpLabel>
                        <CpRadioGroup
                          name={`aws.roleName.value`}
                          vertical
                          options={roles
                            .filter(
                              (option) =>
                                option._cpln.ref === role.value ||
                                option._cpln.label.toLowerCase().includes(role.filter.toLowerCase()) ||
                                option._cpln.ref.toLowerCase().includes(role.filter.toLowerCase()),
                            )
                            .map((option) => ({ label: option._cpln.label, value: option._cpln.ref }))}
                        />
                        <CpErrorWarning name={`aws.roleName.value`} />
                      </CpFormItem>
                    </>
                  )}
                </>
              ) : null}
              {method === "policies" ? (
                <>
                  <Divider orientation={"left"} orientationMargin={0}>
                    Continue with Selecting AWS Policies
                  </Divider>
                  {policyRefsError ? (
                    <>
                      <Alert
                        className="mt-2 mb-4"
                        type={"info"}
                        message={
                          <div>
                            If the policy is AWS managed, use <span className="font-semibold">aws::</span> prefix
                          </div>
                        }
                      />
                      <CpArrayString label="Policy Refs" name={`aws.policyRefs.manualPolicies`} />
                      <Alert
                        className="mt-2"
                        type={"error"}
                        showIcon
                        message={`Selected Cloud Account Failed to List Policies`}
                        description={policyRefsError}
                      />
                    </>
                  ) : (
                    <>
                      {unmatchedPolicies.length > 0 && (
                        <Alert
                          className="mt-2 mb-4"
                          type="warning"
                          showIcon
                          message="Unrecognized Policies"
                          description={
                            <div>
                              <div>The following policies were not found in the AWS managed policy list:</div>
                              <ul className="mt-1 mb-0 pl-4">
                                {unmatchedPolicies.map((ref) => (
                                  <li key={ref}>
                                    <code>{ref}</code>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          }
                        />
                      )}
                      <CpFormItem>
                        <CpLabel name={`aws.policyRefs.filter`}>Filter Policies</CpLabel>
                        <Space.Compact>
                          <CpInput name={`aws.policyRefs.filter`} style={{ width: 300 }} />
                          <Tooltip title={`Refetch Policies`}>
                            <Button
                              icon={<SyncOutlined />}
                              onClick={() => fetchPolicies()}
                              loading={isFetchingPolicies}
                              disabled={isFetchingPolicies}
                            />
                          </Tooltip>
                        </Space.Compact>
                      </CpFormItem>
                      <CpFormItem>
                        <CpLabel name={`aws.policyRefs.selectedPolicies`}></CpLabel>
                        <CpCheckboxGroup name={`aws.policyRefs.selectedPolicies`}>
                          <div className="flex flex-col">
                            {filteredPolicies.length < 1 ? <div className="color-default">No matching policy for the filter</div> : null}
                            {filteredPolicies.map((option) => (
                              <Checkbox key={option._cpln.ref} value={option._cpln.ref}>
                                {option._cpln.label}
                              </Checkbox>
                            ))}
                          </div>
                        </CpCheckboxGroup>
                        <CpErrorWarning name={`aws.policyRefs.selectedPoliciesroot`} />
                      </CpFormItem>
                    </>
                  )}
                </>
              ) : null}
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
